import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { CircleNotch, ArrowClockwise, ArrowLeft, WarningCircle } from "@phosphor-icons/react";

export const REQUEST_TIMEOUT_MS = 10000;

// Prompt 24 — reliable module loading: 10s timeout per step, request
// sequencing (an earlier request never overwrites a later selection),
// abort on route/product change, partial-response awareness.
export function useModuleData(moduleId, requests, deps) {
  const [state, setState] = useState({ data: null, loading: true, currentStep: "", error: null, partial: false });
  const [elapsed, setElapsed] = useState(0);
  const seqRef = useRef(0);
  const ctrlRef = useRef(null);
  const reqRef = useRef(requests);
  reqRef.current = requests;
  const lastKey = `mai_last_loaded_${moduleId}`;

  const run = useCallback(async () => {
    const seq = ++seqRef.current;
    if (ctrlRef.current) ctrlRef.current.abort();
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    const started = Date.now();
    setElapsed(0);
    const timer = setInterval(() => {
      if (seq === seqRef.current) setElapsed(Math.round((Date.now() - started) / 1000));
    }, 1000);
    const reqs = reqRef.current;
    setState({ data: null, loading: true, currentStep: reqs[0]?.label || "Loading", error: null, partial: false });
    const data = {};
    let failedStep = null, failedDetail = null;
    for (const req of reqs) {
      if (seq !== seqRef.current) { clearInterval(timer); return; }
      setState((s) => ({ ...s, currentStep: req.label }));
      try {
        const r = await axios.get(req.url, { signal: ctrl.signal, timeout: REQUEST_TIMEOUT_MS, params: req.params });
        data[req.key] = r.data;
      } catch (e) {
        if (axios.isCancel(e) || e.code === "ERR_CANCELED" || ctrl.signal.aborted) { clearInterval(timer); return; }
        failedStep = req.label;
        failedDetail = e.code === "ECONNABORTED"
          ? `Timed out after ${REQUEST_TIMEOUT_MS / 1000} seconds`
          : (e.response ? `Server error (HTTP ${e.response.status})` : (e.message || "Network error"));
        break;
      }
    }
    clearInterval(timer);
    if (seq !== seqRef.current) return;
    if (failedStep) {
      const reference = `MAI-${moduleId.toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
      console.error(`[${reference}] module '${moduleId}' failed at step "${failedStep}": ${failedDetail}`);
      const partial = Object.keys(data).length > 0;
      setState({ data: partial ? data : null, loading: false, currentStep: failedStep,
                 error: { reference, step: failedStep, detail: failedDetail }, partial });
    } else {
      try { localStorage.setItem(lastKey, new Date().toISOString()); } catch { /* private mode */ }
      setState({ data, loading: false, currentStep: "", error: null, partial: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    return () => { seqRef.current += 1; if (ctrlRef.current) ctrlRef.current.abort(); };
  }, [run]);

  let lastLoadedAt = null;
  try { lastLoadedAt = localStorage.getItem(lastKey); } catch { /* private mode */ }
  return { ...state, elapsed, retry: run, lastLoadedAt };
}

export const ModuleLoadState = ({ loading, currentStep, elapsed, error, retry, lastLoadedAt, partial }) => {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div data-testid="module-loading" className="py-24 text-center">
        <CircleNotch size={22} className="animate-spin inline text-slate-400" />
        <p data-testid="module-loading-step" className="text-sm text-slate-500 mt-3">Loading — {currentStep}…</p>
        <p data-testid="module-loading-elapsed" className="font-mono-data text-[10px] text-slate-400 mt-1">
          {elapsed}s elapsed{elapsed >= 8 ? " — a step that exceeds 10 seconds will stop with a retry option" : ""}
        </p>
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="module-load-error" className="max-w-xl mx-auto my-16 bg-white border border-red-200">
        <div className="px-5 py-4 border-b border-red-100 flex items-center gap-2">
          <WarningCircle size={18} className="text-red-600" />
          <p className="font-heading font-bold text-sm text-red-700">This module could not finish loading</p>
        </div>
        <div className="px-5 py-4 space-y-2 text-xs text-slate-600">
          <p data-testid="error-step"><span className="font-semibold">Failed step:</span> {error.step}</p>
          <p data-testid="error-detail"><span className="font-semibold">What happened:</span> {error.detail}</p>
          {partial && <p data-testid="partial-warning" className="text-amber-700">Some sections loaded before the failure — partial data is shown below this notice.</p>}
          <p data-testid="error-reference" className="font-mono-data text-[10px] text-slate-400">Technical reference: {error.reference}</p>
          <p data-testid="last-loaded-time" className="font-mono-data text-[10px] text-slate-400">
            Last successful load: {lastLoadedAt ? new Date(lastLoadedAt).toLocaleString() : "never on this device"}
          </p>
        </div>
        <div className="px-5 pb-4 flex gap-2">
          <button data-testid="module-retry" onClick={retry}
            className="flex-1 bg-slate-900 text-white text-xs font-semibold py-2.5 hover:bg-slate-800 flex items-center justify-center gap-1.5">
            <ArrowClockwise size={13} /> Retry
          </button>
          <button data-testid="module-return" onClick={() => navigate("/console")}
            className="px-5 text-xs text-slate-600 border border-slate-300 hover:bg-slate-50 flex items-center gap-1.5">
            <ArrowLeft size={13} /> Return
          </button>
        </div>
      </div>
    );
  }
  return null;
};
